import React, { useState, useEffect, useMemo } from "react";
import { X } from "@phosphor-icons/react";
import { todayIso } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBooks } from "@/context/BookContext";
import { Link } from "react-router-dom";

const round2 = (n) => Math.round(n * 100) / 100;

export default function TransactionModal({ open, onClose, onSaved, editing }) {
  const { activeBook, activeId, books } = useBooks();
  const [people, setPeople] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({
    date: todayIso(),
    mode: "cash",
    name: "",
    amount: "",
    purpose: "",
    bank: "",
    sign: "-",
    actual_amount: "",
    commission_pct: "",
    rate: "",
    units: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/people").then(({ data }) => setPeople(data)).catch(() => {});
    api.get("/banks").then(({ data }) => setBanks(data)).catch(() => {});
  }, [open]);

  const isHaya = (activeBook?.name || "").toLowerCase() === "haya";
  const isDtt = form.mode === "dtt";

  // Derived values
  const afterCommission = useMemo(() => {
    if (!isHaya) return null;
    const a = parseFloat(form.actual_amount);
    const pct = parseFloat(form.commission_pct);
    if (isNaN(a) || isNaN(pct)) return null;
    return round2(a - (a * pct) / 100);
  }, [isHaya, form.actual_amount, form.commission_pct]);

  const dttTotal = useMemo(() => {
    if (!isDtt) return null;
    const u = parseFloat(form.units);
    const r = parseFloat(form.rate);
    if (isNaN(u) || isNaN(r)) return null;
    return round2(u * r);
  }, [isDtt, form.units, form.rate]);

  useEffect(() => {
    if (editing) {
      setForm({
        date: editing.date,
        mode: editing.mode,
        name: editing.name,
        amount: String(Math.abs(editing.amount)),
        purpose: editing.purpose || "",
        bank: editing.bank || "",
        sign: editing.amount < 0 ? "-" : "+",
        actual_amount: editing.actual_amount != null ? String(editing.actual_amount) : "",
        commission_pct: editing.commission_pct != null ? String(editing.commission_pct) : "",
        rate: editing.rate != null ? String(editing.rate) : "",
        units: editing.units != null ? String(editing.units) : "",
      });
    } else {
      setForm({
        date: todayIso(), mode: "cash", name: "", amount: "", purpose: "", bank: "", sign: "-",
        actual_amount: "", commission_pct: "", rate: "", units: "",
      });
    }
  }, [editing, open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Determine posted amount
      let base;
      if (isHaya && afterCommission != null) {
        base = afterCommission;
      } else if (isDtt && dttTotal != null) {
        base = dttTotal;
      } else {
        base = Number(form.amount);
      }
      const signed = (form.sign === "-" ? -1 : 1) * Math.abs(base);

      const payload = {
        date: form.date,
        mode: form.mode,
        name: form.name.trim(),
        amount: signed,
        purpose: form.purpose,
        bank: form.bank,
        book_id: activeId || undefined,
        actual_amount: isHaya && form.actual_amount !== "" ? Number(form.actual_amount) : null,
        commission_pct: isHaya && form.commission_pct !== "" ? Number(form.commission_pct) : null,
        rate: isDtt && form.rate !== "" ? Number(form.rate) : null,
        units: isDtt && form.units !== "" ? Number(form.units) : null,
      };
      if (editing) {
        await api.patch(`/transactions/${editing.id}`, payload);
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0f1b4c]/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="tx-modal"
        className="brut-card-lg w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="brut-label text-[#2cb4a6]">{editing ? "Edit entry" : "New entry"}</div>
            <h3 className="font-display font-black text-2xl">
              {editing ? "Update transaction" : "Add transaction"}
            </h3>
            {activeBook && (
              <div className="text-xs text-[#4a5578] mt-1">
                Adding to book: <span className="font-bold text-[#2cb4a6]">{activeBook.name}</span>
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="brut-btn brut-btn-secondary p-2" data-testid="tx-modal-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="brut-label block mb-2">Date</label>
            <input data-testid="tx-date" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className="brut-input" />
          </div>
          <div>
            <label className="brut-label block mb-2">Mode</label>
            <div className="flex gap-2">
              {["cash", "upi", "dtt"].map((m) => (
                <button key={m} type="button" data-testid={`tx-mode-${m}`}
                  onClick={() => set("mode", m)}
                  className={`brut-btn flex-1 text-xs px-2 ${form.mode === m ? "brut-btn-primary" : "brut-btn-secondary"}`}>
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-2">
            <label className="brut-label block mb-2 flex justify-between items-center">
              <span>Name / Category *</span>
              <Link to="/people" className="text-[10px] text-[#2cb4a6] no-underline hover:underline" onClick={onClose}>+ manage people</Link>
            </label>
            <select
              data-testid="tx-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="brut-input"
            >
              <option value="" disabled>— select a person or book —</option>
              {/* Preserve legacy names when editing that aren't in current lists */}
              {editing && form.name &&
                !people.some((p) => p.name === form.name) &&
                !books.some((b) => b.name === form.name) && (
                <option value={form.name}>{form.name} (existing)</option>
              )}
              {people.length > 0 && (
                <optgroup label="People">
                  {people.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}{p.code ? ` · ${p.code}` : ""}</option>
                  ))}
                </optgroup>
              )}
              {books.filter((b) => b.id !== activeId).length > 0 && (
                <optgroup label="Mirror to another book">
                  {books.filter((b) => b.id !== activeId).map((b) => (
                    <option key={`book-${b.id}`} value={b.name}>↔ {b.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <div className="text-[10px] text-[#4a5578] mt-1 leading-relaxed">
              Only names from the list are allowed. Add a new person from the People page.
              Picking a book name (e.g. <b>AK Accounts</b>) auto-creates a mirror entry there.
            </div>
          </div>

          <div>
            <label className="brut-label block mb-2">Direction</label>
            <div className="flex gap-2">
              {[["-","Outflow"],["+","Inflow"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`tx-sign-${v === "-" ? "out" : "in"}`}
                  onClick={() => set("sign", v)}
                  className={`brut-btn flex-1 text-sm ${form.sign === v ? (v === "-" ? "brut-btn-danger" : "brut-btn-primary") : "brut-btn-secondary"}`}>
                  {v} {l}
                </button>
              ))}
            </div>
          </div>
          {!isHaya && !isDtt && (
            <div>
              <label className="brut-label block mb-2">Amount (₹)</label>
              <input data-testid="tx-amount" type="number" min="0" step="0.01" required value={form.amount}
                onChange={(e) => set("amount", e.target.value)} className="brut-input" />
            </div>
          )}
          {isHaya && (
            <>
              <div>
                <label className="brut-label block mb-2">Actual amount (₹)</label>
                <input data-testid="tx-actual-amount" type="number" min="0" step="0.01" required value={form.actual_amount}
                  onChange={(e) => set("actual_amount", e.target.value)} className="brut-input" />
              </div>
              <div>
                <label className="brut-label block mb-2">Commission %</label>
                <input data-testid="tx-commission-pct" type="number" min="0" step="0.01" required value={form.commission_pct}
                  onChange={(e) => set("commission_pct", e.target.value)} className="brut-input" />
              </div>
              <div className="col-span-2 bg-[#e6ecf5] border-2 border-[#0f1b4c] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#4a5578]">After commission</div>
                <div className="font-mono-fin font-bold text-2xl text-[#1e2a6b]" data-testid="tx-after-commission">
                  {afterCommission != null ? `₹${afterCommission.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                </div>
              </div>
            </>
          )}
          {isDtt && (
            <>
              <div>
                <label className="brut-label block mb-2">Entry (units)</label>
                <input data-testid="tx-units" type="number" min="0" step="0.0001" required value={form.units}
                  onChange={(e) => set("units", e.target.value)} className="brut-input" />
              </div>
              <div>
                <label className="brut-label block mb-2">Rate (₹ per unit)</label>
                <input data-testid="tx-rate" type="number" min="0" step="0.01" required value={form.rate}
                  onChange={(e) => set("rate", e.target.value)} className="brut-input" />
              </div>
              <div className="col-span-2 bg-[#cffafe] border-2 border-[#0f1b4c] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#4a5578]">Total (units × rate)</div>
                <div className="font-mono-fin font-bold text-2xl text-[#1e2a6b]" data-testid="tx-dtt-total">
                  {dttTotal != null ? `₹${dttTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                </div>
              </div>
            </>
          )}

          <div className="col-span-2">
            <label className="brut-label block mb-2 flex justify-between items-center">
              <span>Bank (optional)</span>
              <Link to="/people" className="text-[10px] text-[#2cb4a6] no-underline hover:underline" onClick={onClose}>+ manage banks</Link>
            </label>
            <select data-testid="tx-bank" value={form.bank} onChange={(e) => set("bank", e.target.value)} className="brut-input">
              <option value="">— none —</option>
              {banks.map((b) => (<option key={b.id} value={b.name}>{b.name}</option>))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="brut-label block mb-2">Purpose / Note</label>
            <input data-testid="tx-purpose" value={form.purpose} onChange={(e) => set("purpose", e.target.value)}
              placeholder="e.g. ATM Cash withdrawal from Equitas bank" className="brut-input" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="brut-btn brut-btn-secondary" data-testid="tx-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="brut-btn brut-btn-primary" data-testid="tx-save">
            {busy ? "Saving…" : (editing ? "Update" : "Add entry")}
          </button>
        </div>
      </form>
    </div>
  );
}
